import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../redux/store';

/**
 * Get organization slug from Redux state
 * Prefers customSlug over Stytch-generated slug
 * This is the SINGLE SOURCE OF TRUTH for organization slug resolution
 */
export const useOrgSlug = (): string | null => {
    const organization = useAppSelector(state => state.organization.currentOrganization);
    // Prefer custom slug, fall back to Stytch slug
    return organization?.customSlug || organization?.slug || null;
};

/**
 * Get organization slug from organization object
 * Prefers customSlug over Stytch-generated slug
 * Use this when you have the organization object directly
 */
export const getOrgSlug = (organization: { customSlug?: string; slug?: string } | null | undefined): string | null => {
    if (!organization) return null;
    return organization.customSlug || organization.slug || null;
};

/**
 * Build organization home URL
 * @param orgSlug - Organization slug (if not provided, returns '/')
 * @returns Organization home URL (e.g., '/smith-associates')
 */
export const buildOrgHomeUrl = (orgSlug?: string | null): string => {
    if (!orgSlug) return '/';
    return `/${orgSlug}`;
};

/**
 * Build case URL (without chat)
 * @param caseId - Case ID
 * @param orgSlug - Organization slug (if not provided, returns case ID only)
 * @returns Case URL (e.g., '/smith-associates/123')
 */
export const buildCaseUrl = (caseId: string, orgSlug?: string | null): string => {
    if (!orgSlug) return `/${caseId}`;
    return `/${orgSlug}/${caseId}`;
};

/**
 * Build chat URL (includes case and chat)
 * @param caseId - Case ID
 * @param chatId - Chat ID
 * @param orgSlug - Organization slug (if not provided, returns case/chat IDs only)
 * @returns Chat URL (e.g., '/smith-associates/123/456')
 */
export const buildChatUrl = (caseId: string, chatId: string, orgSlug?: string | null): string => {
    if (!orgSlug) return `/${caseId}/${chatId}`;
    return `/${orgSlug}/${caseId}/${chatId}`;
};

/**
 * Build account settings URL
 * @param orgSlug - Organization slug (if not provided, returns '/account-settings')
 * @returns Account settings URL (e.g., '/smith-associates/account-settings')
 */
export const buildAccountSettingsUrl = (orgSlug?: string | null): string => {
    if (!orgSlug) return '/account-settings';
    return `/${orgSlug}/account-settings`;
};

/**
 * Build organization settings URL
 * @param orgSlug - Organization slug (if not provided, returns '/organization-settings')
 * @returns Organization settings URL (e.g., '/smith-associates/organization-settings')
 */
export const buildOrganizationSettingsUrl = (orgSlug?: string | null): string => {
    if (!orgSlug) return '/organization-settings';
    return `/${orgSlug}/organization-settings`;
};

/**
 * Build help / documentation URL
 * @param orgSlug - Organization slug (if not provided, returns '/help')
 * @returns Help URL (e.g., '/smith-associates/help')
 */
export const buildHelpUrl = (orgSlug?: string | null): string => {
    if (!orgSlug) return '/help';
    return `/${orgSlug}/help`;
};

/**
 * Build organization-scoped URL with clean structure
 * @deprecated Use specific URL builders (buildOrgHomeUrl, buildCaseUrl, buildChatUrl) instead
 * @param path - The path to build (e.g., '/123' for case, '/123/456' for chat)
 * @param orgSlug - Optional organization slug (if not provided, returns path as-is)
 * @returns Clean URL (e.g., '/smith-associates/123/456')
 */
export const buildOrgUrl = (path: string, orgSlug?: string): string => {
    if (!orgSlug) return path;

    // Remove leading slash if present
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;

    // Build clean URL: /orgSlug/path
    return `/${orgSlug}${normalizedPath ? `/${normalizedPath}` : ''}`;
};

/**
 * Navigation helper hook that automatically includes organization slug in URLs
 * @returns Navigation function that builds org-scoped clean URLs
 */
export const useOrgNavigate = () => {
    const navigate = useNavigate();
    const orgSlug = useOrgSlug();

    return (path: string, options?: { replace?: boolean }) => {
        const url = buildOrgUrl(path, orgSlug || undefined);
        navigate(url, options);
    };
};
